import React, { useContext, useState, useEffect, useMemo } from "react";
import { Select } from "antd";
import { useFetchApi } from "../../../hooks/useFetchApi";
import { DispatchContext } from "../../../store/context";
import { UPDATE_BASE_CURRENCY } from "../../../store/types";
import {
  getExchangeInfo,
  marketCurrencyMap,
  marketLogoMap,
  STOCK_EXCHANGES,
} from "../../../helper";

interface StockSearchModalProps {
  loading?: boolean;
  selectedStock: any;
  setSelectedStock: (value: any) => void;
}

const DEFAULT_STOCK_ID = 5778;
const STORAGE_KEY = "financials_selected_ticker";

export const StockSelectOptions: React.FC<StockSearchModalProps> = ({
  loading = false,
  selectedStock,
  setSelectedStock,
}) => {
  const [stocks, setStocks] = useState<any[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const [selectedExchange, setSelectedExchange] = useState<string>("all");

  const {
    response,
    loading: stockLoading,
    execute: getStocks,
  } = useFetchApi("/api/all-stocks", {}, "GET");

  // Fetch stocks
  useEffect(() => {
    getStocks();
  }, []);

  // Process stocks
  useEffect(() => {
    if (response && Object.keys(response).length) {
      const stocksFromExchanges = STOCK_EXCHANGES?.flatMap(
        (exchange) =>
          (response &&
            response?.find((section: any) => section?.section === exchange)
              ?.options) ||
          [],
      );

      const activeStocks = stocksFromExchanges.filter(
        (stock) => stock.status === 1,
      );

      setStocks(activeStocks);
    }
  }, [response]);

  const stockExchanges = useMemo(() => {
    const exchanges = new Set<string>();
    stocks?.forEach((stock) => {
      if (stock.market_name) {
        exchanges.add(stock.market_name);
      }
    });
    return Array.from(exchanges).sort();
  }, [stocks]);

  useEffect(() => {
    if (stocks && stocks.length > 0) {
      const filteredStocks =
        selectedExchange === "all"
          ? stocks
          : stocks.filter((stock) => stock.market_name === selectedExchange);

      const formattedOptions = filteredStocks.map((stock) => ({
        value: stock.id,
        label: `${stock.ticker_symbol} ${stock.company_name}`,
        stock: stock,
      }));
      setOptions(formattedOptions);
    }
  }, [stocks, selectedExchange]);

  const dispatch = useContext(DispatchContext);

  const handleChange = (value: number) => {
    if (!value) {
      setSelectedStock(null);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ cleared: true }));
      return;
    }

    const selected = stocks.find((stock) => stock.id === value);

    const currency = marketCurrencyMap[selected?.short_name] ?? "USD";

    dispatch({ type: UPDATE_BASE_CURRENCY, payload: currency });

    setSelectedStock(selected);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ id: value }));
  };

  useEffect(() => {
    if (stocks.length > 0 && !selectedStock) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        const parsed = saved ? JSON.parse(saved) : null;

        if (parsed?.cleared) {
          return;
        }

        const savedId = parsed?.id;
        const savedStock = savedId
          ? stocks.find((stock) => stock.id === savedId)
          : null;

        if (savedStock) {
          handleChange(savedStock.id);
          return;
        }
      } catch {
      }

      const defaultStock = stocks.find(
        (stock) => stock.id === DEFAULT_STOCK_ID,
      );
      if (defaultStock) {
        handleChange(defaultStock.id);
      }
    }
  }, [stocks]);

  const optionRender = (option: any) => {
    const stock = stocks.find((s) => s.id === option.data.value);

    if (!stock) return option.label;

    return (
      <div className="flex flex-col w-full">
        <div className="w-full flex items-center justify-between">
          <div className="flex items-center gap-x-2">
            <img
              src={`/${stock.company_logo}`}
              alt={stock.company_logo}
              className="w-6 h-6 object-contain"
            />
            <span className="font-dm text-sm text-[#344054]">
              {stock.company_name}
            </span>
          </div>
          <div className="flex items-center gap-x-2">
            <img
              src={marketLogoMap[stock?.market_name] ?? ""}
              alt=""
              className="w-6 h-6 object-cover"
            />
            <span className="text-[#344054] font-dm text-sm">
              {stock.short_name}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const labelRender = (props: any) => {
    const { value } = props;
    const stock = stocks.find((s) => s.id === value);

    if (!stock) return value;

    const changeColor =
      stock.change_perc >= 0 ? "text-green-600" : "text-red-600";
    const changeSign = stock.change_perc >= 0 ? "+" : "";

    return (
      <div className="flex items-center gap-2">
        <span className="text-gray-900">{stock.company_name}</span>
        <span className="text-gray-700">•</span>
        <span className="text-gray-900">{stock.ticker_symbol}</span>
        <span className="text-gray-700">•</span>
        <span className="text-gray-900">{stock.closed}</span>
        <span className={`font-medium ${changeColor}`}>
          ({changeSign}
          {stock.change_perc}%)
        </span>
      </div>
    );
  };

  // Custom dropdown render to add exchange filters at the top
  const dropdownRender = (menu: React.ReactNode) => (
    <>
      {/* Exchange Filter Buttons */}
      <div className="vi-fin-exchange-bar p-3 sticky top-0 z-10">
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedExchange("all");
            }}
            className={`
              px-3 py-1.5 rounded-[9px] text-xs font-medium transition-all duration-200
              ${
                selectedExchange === "all"
                  ? "bg-blue-600 text-white shadow-md"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }
            `}
          >
            All Exchanges
          </button>
          {stockExchanges.map((exchange) => {
            const { logo, short } = getExchangeInfo(exchange);
            return (
              <button
                key={exchange}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedExchange(exchange);
                }}
                className={`
                  px-3 py-1.5 rounded-[9px] text-xs font-medium transition-all duration-200
                  flex items-center gap-2
                  ${
                    selectedExchange === exchange
                      ? "bg-blue-600 text-white shadow-md"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }
                `}
              >
                {logo && (
                  <img
                    src={logo}
                    alt={short}
                    className="w-4 h-4 object-cover"
                  />
                )}
                {short}
              </button>
            );
          })}
        </div>
      </div>
      {/* Stock options */}
      {menu}
    </>
  );

  return (
    <div className="relative !w-full">
      {/* Stock Select Dropdown */}
      <Select
        showSearch
        allowClear
        placeholder="Search by ticker or company name"
        style={{ width: "100%", height: 40 }}
        value={selectedStock?.id || undefined}
        onChange={handleChange}
        loading={stockLoading}
        optionFilterProp="label"
        options={options}
        optionRender={optionRender}
        labelRender={labelRender}
        dropdownRender={dropdownRender}
        popupClassName="vi-fin-stock-popup"
        notFoundContent={
          stockLoading ? (
            <div className="p-3 text-center text-gray-500">Loading...</div>
          ) : (
            <div className="p-3 text-center text-gray-500">No stocks found</div>
          )
        }
      />
    </div>
  );
};
